import { createNodeId, type BehaviorDocument, type BTNode, type BTNodeId } from "./types";
import { createDefaultBtNode } from "../components/behavior-editor/nodeFactory";

export type CompositeKind = "sequence" | "selector" | "retry" | "repeat";

/** Human-friendly base names for auto-assigned labels (e.g. "Sequence 1"). */
const KIND_BASE_LABELS: Record<string, string> = {
  sequence: "Sequence",
  selector: "Fallback",
  retry: "Retry",
  repeat: "Repeat",
  ifThenElse: "If / Then / Else",
  servoPickPlace: "Pick & place",
  moveEeToXyz: "Move EE",
  moveEeByOffset: "Move EE offset",
  runTrainedModel: "Trained model",
  openGripper: "Open gripper",
  closeGripper: "Close gripper",
  goToKeyframe: "Keyframe",
  wait: "Wait",
  randomizeObjectPose: "Randomize object",
  setObjectPose: "Set object pose",
  resetToBaseline: "Reset baseline",
  markSuccess: "Mark success",
  markFailure: "Mark failure",
  conditionObjectGrasped: "If object lifted",
  conditionEeNear: "If EE near",
  conditionObjectNear: "If object near",
  conditionTimeElapsed: "If time elapsed",
};

function isConditionKind(kind: BTNode["kind"]): boolean {
  return (
    kind === "conditionObjectGrasped" ||
    kind === "conditionEeNear" ||
    kind === "conditionObjectNear" ||
    kind === "conditionTimeElapsed"
  );
}

function firstOpenIfThenElseSlot(node: BTNode & { kind: "ifThenElse" }): 0 | 1 | 2 | null {
  if (!node.conditionChild) return 0;
  if (!node.thenChild) return 1;
  if (!node.elseChild) return 2;
  return null;
}

/**
 * Next unused "Kind N" label for the given kind in this doc. Considers both
 * existing labels ("Sequence 3") and the implicit default names of unlabeled
 * same-kind nodes, so counters stay in sync with what the user sees.
 */
export function nextAutoLabelForKind(doc: BehaviorDocument, kind: string): string {
  const base = KIND_BASE_LABELS[kind] || kind;
  const used = new Set<number>();
  Object.values(doc.nodes).forEach((n) => {
    if (n.kind !== kind) return;
    const source = n.label && n.label.trim().length > 0 ? n.label.trim() : base;
    const m = source.match(/^(.*?)(?:\s+(\d+))?$/);
    if (!m) return;
    const labelBase = m[1];
    if (labelBase !== base) return;
    const num = m[2] ? parseInt(m[2], 10) : 1;
    if (Number.isFinite(num) && num > 0) used.add(num);
  });
  let n = 1;
  while (used.has(n)) n += 1;
  return `${base} ${n}`;
}

function findParentId(doc: BehaviorDocument, nodeId: BTNodeId): BTNodeId | null {
  for (const [id, node] of Object.entries(doc.nodes)) {
    if (node.kind === "sequence" || node.kind === "selector") {
      if (node.children.includes(nodeId)) return id;
    } else if ((node.kind === "retry" || node.kind === "repeat") && node.child === nodeId) {
      return id;
    } else if (node.kind === "ifThenElse") {
      if (node.conditionChild === nodeId || node.thenChild === nodeId || node.elseChild === nodeId)
        return id;
    }
  }
  return null;
}

function collectDescendantIds(doc: BehaviorDocument, rootId: BTNodeId): Set<BTNodeId> {
  const out = new Set<BTNodeId>();
  const stack: BTNodeId[] = [rootId];
  while (stack.length > 0) {
    const id = stack.pop();
    if (!id || out.has(id)) continue;
    out.add(id);
    const n = doc.nodes[id];
    if (!n) continue;
    if (n.kind === "sequence" || n.kind === "selector") {
      n.children.forEach((c) => stack.push(c));
    } else if ((n.kind === "retry" || n.kind === "repeat") && n.child) {
      stack.push(n.child);
    } else if (n.kind === "ifThenElse") {
      if (n.conditionChild) stack.push(n.conditionChild);
      if (n.thenChild) stack.push(n.thenChild);
      if (n.elseChild) stack.push(n.elseChild);
    }
  }
  return out;
}

function insertExistingChild(
  doc: BehaviorDocument,
  parentId: BTNodeId,
  index: number,
  childId: BTNodeId
): BehaviorDocument {
  const parent = doc.nodes[parentId];
  if (!parent || !doc.nodes[childId]) return doc;
  if (parent.kind === "sequence" || parent.kind === "selector") {
    const clamped = Math.max(0, Math.min(index, parent.children.length));
    const nextChildren = [...parent.children];
    nextChildren.splice(clamped, 0, childId);
    return {
      ...doc,
      nodes: {
        ...doc.nodes,
        [parentId]: { ...parent, children: nextChildren },
      },
    };
  }
  if (parent.kind === "retry" || parent.kind === "repeat") {
    if (parent.child) return doc;
    return {
      ...doc,
      nodes: {
        ...doc.nodes,
        [parentId]: { ...parent, child: childId },
      },
    };
  }
  if (parent.kind === "ifThenElse") {
    const targetSlot =
      index === 0 || index === 1 || index === 2 ? index : firstOpenIfThenElseSlot(parent);
    if (targetSlot == null) return doc;
    const child = doc.nodes[childId];
    if (targetSlot === 0 && !isConditionKind(child.kind)) return doc;
    if (targetSlot === 0 && parent.conditionChild) return doc;
    if (targetSlot === 1 && parent.thenChild) return doc;
    if (targetSlot === 2 && parent.elseChild) return doc;
    const nextParent =
      targetSlot === 0
        ? { ...parent, conditionChild: childId }
        : targetSlot === 1
          ? { ...parent, thenChild: childId }
          : { ...parent, elseChild: childId };
    return { ...doc, nodes: { ...doc.nodes, [parentId]: nextParent } };
  }
  return doc;
}

/**
 * Insert a freshly created node (by kind) into a parent slot.
 * - sequence/selector: splice at `index` (clamped to [0, children.length]).
 * - retry/repeat: only succeeds if the `child` slot is empty. `index` is ignored.
 * - ifThenElse: fills one of 3 fixed slots based on `index`:
 *   `0 -> condition`, `1 -> then`, `2 -> else`.
 *   When index is out of range, inserts into the first open slot.
 *   Condition slot only accepts condition node kinds.
 * Returns a new document, or the original if the insertion is not legal.
 */
export function insertChild(
  doc: BehaviorDocument,
  parentId: BTNodeId,
  index: number,
  kind: string,
  overrides?: Partial<BTNode> | null
): BehaviorDocument {
  const parent = doc.nodes[parentId];
  if (!parent) return doc;

  const baseFresh = createDefaultBtNode(kind);
  const merged =
    overrides && typeof overrides === "object"
      ? ({
          ...baseFresh,
          ...overrides,
          id: baseFresh.id,
          kind: baseFresh.kind,
        } as BTNode)
      : baseFresh;
  const labelFromOverrides =
    overrides && typeof overrides.label === "string" && overrides.label.trim()
      ? overrides.label.trim()
      : null;
  const fresh: BTNode = {
    ...merged,
    label: labelFromOverrides ?? nextAutoLabelForKind(doc, baseFresh.kind),
  } as BTNode;

  if (parent.kind === "sequence" || parent.kind === "selector") {
    const clamped = Math.max(0, Math.min(index, parent.children.length));
    const nextChildren = [...parent.children];
    nextChildren.splice(clamped, 0, fresh.id);
    return {
      ...doc,
      nodes: {
        ...doc.nodes,
        [parentId]: { ...parent, children: nextChildren },
        [fresh.id]: fresh,
      },
    };
  }

  if (parent.kind === "retry" || parent.kind === "repeat") {
    if (parent.child) return doc;
    return {
      ...doc,
      nodes: {
        ...doc.nodes,
        [parentId]: { ...parent, child: fresh.id },
        [fresh.id]: fresh,
      },
    };
  }

  if (parent.kind === "ifThenElse") {
    const targetSlot =
      index === 0 || index === 1 || index === 2 ? index : firstOpenIfThenElseSlot(parent);
    if (targetSlot == null) return doc;
    if (targetSlot === 0 && !isConditionKind(fresh.kind)) return doc;
    if (targetSlot === 0 && parent.conditionChild) return doc;
    if (targetSlot === 1 && parent.thenChild) return doc;
    if (targetSlot === 2 && parent.elseChild) return doc;
    const nextParent =
      targetSlot === 0
        ? { ...parent, conditionChild: fresh.id }
        : targetSlot === 1
          ? { ...parent, thenChild: fresh.id }
          : { ...parent, elseChild: fresh.id };
    return {
      ...doc,
      nodes: {
        ...doc.nodes,
        [parentId]: nextParent,
        [fresh.id]: fresh,
      },
    };
  }

  return doc;
}

/**
 * Inserts a teaching template for conditional branching:
 * Fallback[
 *   Sequence[Condition(TimeElapsed), Wait],
 *   Wait
 * ]
 */
export function insertIfThenElseTemplate(
  doc: BehaviorDocument,
  parentId: BTNodeId,
  index: number
): BehaviorDocument {
  const fallback = createDefaultBtNode("selector");
  const sequence = createDefaultBtNode("sequence");
  const condition = createDefaultBtNode("conditionTimeElapsed");
  const thenWait = createDefaultBtNode("wait");
  const elseWait = createDefaultBtNode("wait");

  const fallbackNode: BTNode = { ...fallback, label: nextAutoLabelForKind(doc, "selector") };
  const sequenceNode: BTNode = {
    ...sequence,
    label: nextAutoLabelForKind(doc, "sequence"),
    children: [condition.id, thenWait.id],
  };
  const conditionNode: BTNode = {
    ...condition,
    label: nextAutoLabelForKind(doc, "conditionTimeElapsed"),
    seconds: 1,
  };
  const thenNode: BTNode = { ...thenWait, label: "Then action", seconds: 0.1 };
  const elseNode: BTNode = { ...elseWait, label: "Else action", seconds: 0.1 };

  const seededDoc: BehaviorDocument = {
    ...doc,
    nodes: {
      ...doc.nodes,
      [fallbackNode.id]: { ...fallbackNode, children: [sequenceNode.id, elseNode.id] } as BTNode,
      [sequenceNode.id]: sequenceNode,
      [conditionNode.id]: conditionNode,
      [thenNode.id]: thenNode,
      [elseNode.id]: elseNode,
    },
  };
  return insertExistingChild(seededDoc, parentId, index, fallbackNode.id);
}

/**
 * Reorder a child within a sequence/selector parent. No-op if `parent` is not
 * a sequence/selector, or if `from` is out of range.
 */
export function moveChild(
  doc: BehaviorDocument,
  parentId: BTNodeId,
  from: number,
  to: number
): BehaviorDocument {
  const parent = doc.nodes[parentId];
  if (!parent) return doc;
  if (parent.kind !== "sequence" && parent.kind !== "selector") return doc;
  const len = parent.children.length;
  if (from < 0 || from >= len) return doc;
  const clampedTo = Math.max(0, Math.min(to, len - 1));
  if (clampedTo === from) return doc;
  const next = [...parent.children];
  const [moved] = next.splice(from, 1);
  next.splice(clampedTo, 0, moved);
  return {
    ...doc,
    nodes: { ...doc.nodes, [parentId]: { ...parent, children: next } },
  };
}

/**
 * Delete a node and its entire subtree. Also detaches the deleted node from
 * each parent it appears under. Deleting the root is rejected.
 */
export function deleteNodeFromBehavior(doc: BehaviorDocument, nodeId: BTNodeId): BehaviorDocument {
  if (!doc.nodes[nodeId]) return doc;
  if (doc.rootId === nodeId) return doc;

  const toRemove = collectDescendantIds(doc, nodeId);
  const nextNodes: Record<BTNodeId, BTNode> = {};
  Object.entries(doc.nodes).forEach(([id, node]) => {
    if (toRemove.has(id)) return;
    if (node.kind === "sequence" || node.kind === "selector") {
      if (node.children.some((c) => toRemove.has(c))) {
        nextNodes[id] = {
          ...node,
          children: node.children.filter((c) => !toRemove.has(c)),
        };
        return;
      }
    } else if (node.kind === "retry" || node.kind === "repeat") {
      if (node.child && toRemove.has(node.child)) {
        nextNodes[id] = { ...node, child: null };
        return;
      }
    } else if (node.kind === "ifThenElse") {
      const nextCondition =
        node.conditionChild && toRemove.has(node.conditionChild) ? null : node.conditionChild;
      const nextThen = node.thenChild && toRemove.has(node.thenChild) ? null : node.thenChild;
      const nextElse = node.elseChild && toRemove.has(node.elseChild) ? null : node.elseChild;
      if (
        nextCondition !== node.conditionChild ||
        nextThen !== node.thenChild ||
        nextElse !== node.elseChild
      ) {
        nextNodes[id] = {
          ...node,
          conditionChild: nextCondition,
          thenChild: nextThen,
          elseChild: nextElse,
        };
        return;
      }
    }
    nextNodes[id] = node;
  });

  return { ...doc, nodes: nextNodes };
}

/**
 * Change a composite node's kind while preserving content when possible.
 * Sequence/selector with multiple children -> retry/repeat: inserts an inner
 * sequence node so a single subtree child is still valid for retry/repeat.
 */
export function convertCompositeKind(
  doc: BehaviorDocument,
  nodeId: BTNodeId,
  newKind: CompositeKind
): BehaviorDocument | null {
  const n = doc.nodes[nodeId];
  if (!n) return null;
  if (n.kind !== "sequence" && n.kind !== "selector" && n.kind !== "retry" && n.kind !== "repeat") {
    return null;
  }
  if (n.kind === newKind) return doc;

  if (
    (n.kind === "sequence" || n.kind === "selector") &&
    (newKind === "sequence" || newKind === "selector")
  ) {
    return { ...doc, nodes: { ...doc.nodes, [nodeId]: { ...n, kind: newKind } as BTNode } };
  }

  if (n.kind === "retry" && newKind === "repeat") {
    return {
      ...doc,
      nodes: {
        ...doc.nodes,
        [nodeId]: {
          id: n.id,
          kind: "repeat",
          count: Math.max(1, n.maxAttempts),
          child: n.child,
        } as BTNode,
      },
    };
  }
  if (n.kind === "repeat" && newKind === "retry") {
    return {
      ...doc,
      nodes: {
        ...doc.nodes,
        [nodeId]: {
          id: n.id,
          kind: "retry",
          maxAttempts: Math.max(1, n.count),
          child: n.child,
        } as BTNode,
      },
    };
  }

  if (newKind === "sequence" || newKind === "selector") {
    if (n.kind === "retry" || n.kind === "repeat") {
      const children = n.child ? [n.child] : [];
      return {
        ...doc,
        nodes: { ...doc.nodes, [nodeId]: { id: n.id, kind: newKind, children } as BTNode },
      };
    }
  }

  if (newKind === "retry" || newKind === "repeat") {
    if (n.kind === "sequence" || n.kind === "selector") {
      const ch = n.children;
      let childId: BTNodeId | null;
      const nextNodes: Record<BTNodeId, BTNode> = { ...doc.nodes };

      if (ch.length === 0) {
        childId = null;
      } else if (ch.length === 1) {
        childId = ch[0];
      } else {
        const innerId = createNodeId();
        nextNodes[innerId] = { id: innerId, kind: "sequence", children: [...ch] };
        childId = innerId;
      }

      nextNodes[nodeId] =
        newKind === "retry"
          ? ({ id: n.id, kind: "retry", maxAttempts: 3, child: childId } as BTNode)
          : ({ id: n.id, kind: "repeat", count: 3, child: childId } as BTNode);

      return { ...doc, nodes: nextNodes };
    }
  }

  return null;
}

/**
 * Wrap `nodeId` inside a fresh retry or repeat node. The new wrapper takes
 * `nodeId`'s position (its parent's child slot or the document root), and
 * `nodeId` becomes the wrapper's single child. Returns the original doc when
 * the node is missing.
 */
export function wrapNodeInComposite(
  doc: BehaviorDocument,
  nodeId: BTNodeId,
  kind: "retry" | "repeat"
): BehaviorDocument {
  if (!doc.nodes[nodeId]) return doc;
  const wrapperId = createNodeId();
  const wrapper: BTNode =
    kind === "retry"
      ? {
          id: wrapperId,
          kind: "retry",
          maxAttempts: 3,
          child: nodeId,
          label: nextAutoLabelForKind(doc, "retry"),
        }
      : {
          id: wrapperId,
          kind: "repeat",
          count: 3,
          child: nodeId,
          label: nextAutoLabelForKind(doc, "repeat"),
        };

  const parentId = findParentId(doc, nodeId);
  const nextNodes: Record<BTNodeId, BTNode> = { ...doc.nodes, [wrapperId]: wrapper };
  let nextRootId = doc.rootId;

  if (parentId) {
    const parent = nextNodes[parentId];
    if (parent.kind === "sequence" || parent.kind === "selector") {
      nextNodes[parentId] = {
        ...parent,
        children: parent.children.map((c) => (c === nodeId ? wrapperId : c)),
      };
    } else if (parent.kind === "retry" || parent.kind === "repeat") {
      nextNodes[parentId] = { ...parent, child: wrapperId };
    } else if (parent.kind === "ifThenElse") {
      nextNodes[parentId] = {
        ...parent,
        conditionChild: parent.conditionChild === nodeId ? wrapperId : parent.conditionChild,
        thenChild: parent.thenChild === nodeId ? wrapperId : parent.thenChild,
        elseChild: parent.elseChild === nodeId ? wrapperId : parent.elseChild,
      };
    }
  } else if (doc.rootId === nodeId) {
    nextRootId = wrapperId;
  }

  return { ...doc, nodes: nextNodes, rootId: nextRootId };
}

/**
 * Remove a retry/repeat wrapper and promote its single child into the wrapper's
 * slot. No-op when the wrapper has no child (there's nothing to promote) or
 * when called on a non-decorator. The deleted wrapper is removed from the
 * nodes map.
 */
export function unwrapComposite(doc: BehaviorDocument, nodeId: BTNodeId): BehaviorDocument {
  const n = doc.nodes[nodeId];
  if (!n) return doc;
  if (n.kind !== "retry" && n.kind !== "repeat") return doc;
  if (!n.child || !doc.nodes[n.child]) return doc;

  const childId = n.child;
  const parentId = findParentId(doc, nodeId);
  const nextNodes: Record<BTNodeId, BTNode> = { ...doc.nodes };
  delete nextNodes[nodeId];
  let nextRootId = doc.rootId;

  if (parentId) {
    const parent = nextNodes[parentId];
    if (parent.kind === "sequence" || parent.kind === "selector") {
      nextNodes[parentId] = {
        ...parent,
        children: parent.children.map((c) => (c === nodeId ? childId : c)),
      };
    } else if (parent.kind === "retry" || parent.kind === "repeat") {
      nextNodes[parentId] = { ...parent, child: childId };
    } else if (parent.kind === "ifThenElse") {
      nextNodes[parentId] = {
        ...parent,
        conditionChild: parent.conditionChild === nodeId ? childId : parent.conditionChild,
        thenChild: parent.thenChild === nodeId ? childId : parent.thenChild,
        elseChild: parent.elseChild === nodeId ? childId : parent.elseChild,
      };
    }
  } else if (doc.rootId === nodeId) {
    nextRootId = childId;
  }

  return { ...doc, nodes: nextNodes, rootId: nextRootId };
}

/**
 * Move `nodeId` (and its subtree) under `newParentId`. Rejected when:
 * - moving a node into its own descendant (would create a cycle),
 * - `newParentId` is a leaf (can't host children),
 * - `newParentId` is a retry/repeat whose single child slot is already taken
 *   by a different node.
 *
 * For sequence/selector parents, `insertIndex` is clamped into `[0, children.length]`.
 * No-op if `nodeId` is already at that exact position in `newParentId`.
 */
export function reparentNode(
  doc: BehaviorDocument,
  nodeId: BTNodeId,
  newParentId: BTNodeId,
  insertIndex: number = Number.MAX_SAFE_INTEGER
): BehaviorDocument {
  if (nodeId === newParentId) return doc;
  if (nodeId === doc.rootId) return doc;
  const node = doc.nodes[nodeId];
  const newParent = doc.nodes[newParentId];
  if (!node || !newParent) return doc;

  const descendants = collectDescendantIds(doc, nodeId);
  if (descendants.has(newParentId)) return doc;

  const isComposite =
    newParent.kind === "sequence" ||
    newParent.kind === "selector" ||
    newParent.kind === "retry" ||
    newParent.kind === "repeat" ||
    newParent.kind === "ifThenElse";
  if (!isComposite) return doc;

  if (
    (newParent.kind === "retry" || newParent.kind === "repeat") &&
    newParent.child &&
    newParent.child !== nodeId
  ) {
    return doc;
  }
  if (newParent.kind === "ifThenElse") {
    const openSlot = firstOpenIfThenElseSlot(newParent);
    const alreadyInside =
      newParent.conditionChild === nodeId ||
      newParent.thenChild === nodeId ||
      newParent.elseChild === nodeId;
    if (!alreadyInside && openSlot == null) return doc;
    const childNode = doc.nodes[nodeId];
    if (!childNode) return doc;
    const isTargetConditionSlot =
      (openSlot === 0 && !alreadyInside) || newParent.conditionChild === nodeId;
    if (isTargetConditionSlot && !isConditionKind(childNode.kind)) return doc;
  }

  const currentParentId = findParentId(doc, nodeId);
  const nextNodes: Record<BTNodeId, BTNode> = { ...doc.nodes };

  if (currentParentId) {
    const p = nextNodes[currentParentId];
    if (p.kind === "sequence" || p.kind === "selector") {
      nextNodes[currentParentId] = {
        ...p,
        children: p.children.filter((c) => c !== nodeId),
      };
    } else if ((p.kind === "retry" || p.kind === "repeat") && p.child === nodeId) {
      nextNodes[currentParentId] = { ...p, child: null };
    } else if (p.kind === "ifThenElse") {
      nextNodes[currentParentId] = {
        ...p,
        conditionChild: p.conditionChild === nodeId ? null : p.conditionChild,
        thenChild: p.thenChild === nodeId ? null : p.thenChild,
        elseChild: p.elseChild === nodeId ? null : p.elseChild,
      };
    }
  }

  const np = nextNodes[newParentId];
  if (np.kind === "sequence" || np.kind === "selector") {
    const baseChildren = np.children.filter((c) => c !== nodeId);
    const clamped = Math.max(0, Math.min(insertIndex, baseChildren.length));
    const nextChildren = [...baseChildren];
    nextChildren.splice(clamped, 0, nodeId);
    nextNodes[newParentId] = { ...np, children: nextChildren };
  } else if (np.kind === "retry" || np.kind === "repeat") {
    nextNodes[newParentId] = { ...np, child: nodeId };
  } else if (np.kind === "ifThenElse") {
    if (np.conditionChild === nodeId) {
      nextNodes[newParentId] = { ...np, conditionChild: nodeId };
    } else if (np.thenChild === nodeId) {
      nextNodes[newParentId] = { ...np, thenChild: nodeId };
    } else if (np.elseChild === nodeId) {
      nextNodes[newParentId] = { ...np, elseChild: nodeId };
    } else if (!np.conditionChild) {
      nextNodes[newParentId] = { ...np, conditionChild: nodeId };
    } else if (!np.thenChild) {
      nextNodes[newParentId] = { ...np, thenChild: nodeId };
    } else if (!np.elseChild) {
      nextNodes[newParentId] = { ...np, elseChild: nodeId };
    } else {
      return doc;
    }
  }

  return { ...doc, nodes: nextNodes };
}

/**
 * Returns every composite node id under which `nodeId`'s subtree could legally
 * be re-parented. Excludes the node itself, its descendants (would create a
 * cycle), leaf nodes, and retry/repeat decorators whose child slot is already
 * occupied by a different node.
 */
export function getLegalReparentTargets(doc: BehaviorDocument, nodeId: BTNodeId): BTNodeId[] {
  if (!doc.nodes[nodeId]) return [];
  const blocked = collectDescendantIds(doc, nodeId);
  const out: BTNodeId[] = [];
  Object.entries(doc.nodes).forEach(([id, node]) => {
    if (blocked.has(id)) return;
    if (node.kind === "sequence" || node.kind === "selector") {
      out.push(id);
    } else if (node.kind === "retry" || node.kind === "repeat") {
      if (!node.child || node.child === nodeId) out.push(id);
    } else if (node.kind === "ifThenElse") {
      const candidate = doc.nodes[nodeId];
      if (!candidate) return;
      const alreadyInside =
        node.conditionChild === nodeId || node.thenChild === nodeId || node.elseChild === nodeId;
      if (alreadyInside) {
        out.push(id);
        return;
      }
      const openSlot = firstOpenIfThenElseSlot(node);
      if (openSlot == null) return;
      if (openSlot === 0 && !isConditionKind(candidate.kind)) return;
      out.push(id);
    }
  });
  return out;
}
